/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import type {
  ProductStatusBadgeInfo as ProductCardStatusBadgeInfo,
  ProductStockInfo as ProductCardStockInfo,
} from "@farmlands/commerce";
import { ProductAvailability } from "@farmlands/farmlandspro-api";

import { CartDeliveryMode } from "../../cart/storage/cart.types";
import {
  StockAvailabilityCodes,
  StockAvailabilityStatusType,
} from "../components/StockAvailability/StockAvailabilityStatus";
import { LifeCycleState, StockAvailabilityType } from "../storage";

import {
  availabilityForStore,
  DELIVERY_LOCATION_PREFIX,
  FarmerAccountContext,
  getActiveStockAvailability,
  getLifecycleStatusBadge,
  mapInventoryStatusToStockInfo,
} from "./stock-status";

export interface ShopAvailabilityParams {
  productId: number;
  lifeCycleState: string | null | undefined;
  basePriceExcGst: number | undefined;
  /** Product-level stock availability string, e.g. `"Enquire"`. */
  stockAvailabilityField?: string;
  stockAvailabilities: StockAvailabilityType[];
  account: FarmerAccountContext;
  cartDeliveryMode?: CartDeliveryMode;
  pickupStore?: { inventoryLocationId?: string; orgUnitName?: string };
  cartItems?: { productId: number; quantity: number }[];
  isUrgentDelivery?: boolean;
  /** Scopes stock to a specific store. */
  storeId?: string;
}

export interface ShopAvailabilityDisplay {
  stock?: ProductCardStockInfo;
  lifecycleNotice?: ProductCardStatusBadgeInfo;
}

interface AvailabilityContext {
  isTfo: boolean;
  stockAvailability: StockAvailabilityType | undefined;
  isDelivery: boolean;
  cartQuantity: number;
  hasChosenDeliveryMode: boolean;
  hasStockInHomeBranch: boolean;
  hasStockInSelectedBranch: boolean;
}

const getAvailabilityContext = (params: ShopAvailabilityParams): AvailabilityContext => {
  const { productId, cartDeliveryMode, cartItems, stockAvailabilities, account, pickupStore } =
    params;

  const isTfo: boolean = !!account.hasTfoAccess;
  const stockAvailability: StockAvailabilityType | undefined = getActiveStockAvailability(params);
  const isDelivery: boolean = cartDeliveryMode === CartDeliveryMode.DELIVERY;
  const cartQuantity: number =
    cartItems?.find((entry) => entry.productId === productId)?.quantity ?? 0;

  const hasChosenDeliveryMode: boolean =
    isDelivery || (cartDeliveryMode === CartDeliveryMode.PICKUP && !!pickupStore?.orgUnitName);

  const hasStockInHomeBranch: boolean = stockAvailabilities.some(
    (entry) =>
      entry.inventLocationId === `${DELIVERY_LOCATION_PREFIX}-${account.homeBranchId}` &&
      entry.productId === productId &&
      entry.quantity > 0
  );

  const hasStockInSelectedBranch: boolean = stockAvailabilities.some(
    (entry) =>
      entry.inventLocationId === pickupStore?.inventoryLocationId &&
      entry.productId === productId &&
      entry.quantity > 0
  );

  return {
    isTfo,
    stockAvailability,
    isDelivery,
    cartQuantity,
    hasChosenDeliveryMode,
    hasStockInHomeBranch,
    hasStockInSelectedBranch,
  };
};

const getStock = (
  params: ShopAvailabilityParams,
  context: AvailabilityContext
): ProductCardStockInfo | undefined => {
  const { productId, lifeCycleState, stockAvailabilities, stockAvailabilityField, storeId } =
    params;
  const { isTfo, stockAvailability, isDelivery, cartQuantity, hasChosenDeliveryMode } = context;

  if (storeId) {
    const availability: StockAvailabilityType | undefined = availabilityForStore(
      stockAvailabilities,
      productId,
      storeId
    );
    if (
      !availability ||
      (lifeCycleState === LifeCycleState.TEMPORARILY_UNAVAILABLE && availability.quantity <= 0)
    ) {
      return undefined;
    }

    return mapInventoryStatusToStockInfo(
      {
        code: availability.inventoryLevelCode as StockAvailabilityCodes,
        label: availability.inventoryLevelLabel,
        quantity: availability.quantity,
      },
      availability.isBackOrderAllowed,
      isTfo
    );
  }

  if (!hasChosenDeliveryMode || !stockAvailability) {
    return undefined;
  }

  let stockStatus: StockAvailabilityStatusType | undefined;

  if (isTfo) {
    if (!(
      lifeCycleState === LifeCycleState.TEMPORARILY_UNAVAILABLE && stockAvailability.quantity <= 0
    )) {
      stockStatus = {
        code: stockAvailability.inventoryLevelCode as StockAvailabilityCodes,
        label: stockAvailability.inventoryLevelLabel,
        quantity: stockAvailability.quantity,
      };
    }
  } else if (stockAvailabilityField !== "Enquire" && !isDelivery) {
    // Shareholder pickup: in-stock only when qty covers cart quantity.
    if (stockAvailability.quantity > 0 && stockAvailability.quantity >= cartQuantity) {
      stockStatus = {
        code: StockAvailabilityCodes.IN_STOCK,
        label: "In Stock",
        quantity: stockAvailability.quantity,
      };
    }
  }

  return stockStatus
    ? mapInventoryStatusToStockInfo(stockStatus, stockAvailability.isBackOrderAllowed, isTfo)
    : undefined;
};

const getLifecycleNotice = (
  params: ShopAvailabilityParams,
  context: AvailabilityContext
): ProductCardStatusBadgeInfo | undefined => {
  const {
    productId,
    lifeCycleState,
    basePriceExcGst,
    stockAvailabilities,
    stockAvailabilityField,
    storeId,
    isUrgentDelivery,
  } = params;
  const {
    isTfo,
    stockAvailability,
    isDelivery,
    cartQuantity,
    hasChosenDeliveryMode,
    hasStockInHomeBranch,
    hasStockInSelectedBranch,
  } = context;

  if (storeId) {
    if (isTfo && lifeCycleState !== LifeCycleState.STOCKED) {
      const availability: Required<ProductAvailability> | undefined = availabilityForStore(
        stockAvailabilities,
        productId,
        storeId
      );

      if (!availability || availability.quantity <= 0) {
        return getLifecycleStatusBadge(lifeCycleState, basePriceExcGst, isTfo);
      }
    }

    return undefined;
  }

  if (hasChosenDeliveryMode && isTfo) {
    if (lifeCycleState !== LifeCycleState.STOCKED) {
      const showLifecycle =
        (isDelivery && !hasStockInHomeBranch) || (!isDelivery && !hasStockInSelectedBranch);

      if (showLifecycle) {
        return getLifecycleStatusBadge(lifeCycleState, basePriceExcGst, isTfo);
      }
    }
    return undefined;
  }

  if (hasChosenDeliveryMode && stockAvailabilityField !== "Enquire") {
    if (isDelivery) {
      if (
        !isUrgentDelivery ||
        !stockAvailability ||
        (stockAvailability.quantity > 0 && stockAvailability.quantity >= cartQuantity)
      ) {
        return undefined;
      }

      if (lifeCycleState === LifeCycleState.TEMPORARILY_UNAVAILABLE) {
        return {
          label: "Temporarily unavailable",
          subtitle: "We will contact you for fulfilment options.",
        };
      }

      if (stockAvailability.quantity <= 0) {
        return { label: "Urgent delivery not available" };
      }

      return {
        label: "",
        subtitle:
          "Full quantity can't be delivered urgently, we'll contact you for fulfilment options",
      };
    }

    if (stockAvailability) {
      if (stockAvailability.quantity > 0 && stockAvailability.quantity >= cartQuantity) {
        return undefined;
      }

      return {
        label: "Not available for pick up",
        subtitle: "We will contact you for fulfilment options",
      };
    }
  }

  return undefined;
};

export const getShopAvailabilityDisplay = (
  params: ShopAvailabilityParams
): ShopAvailabilityDisplay => {
  const context: AvailabilityContext = getAvailabilityContext(params);
  const stock: ProductCardStockInfo | undefined = getStock(params, context);
  let lifecycleNotice: ProductCardStatusBadgeInfo | undefined = getLifecycleNotice(params, context);

  if (lifecycleNotice?.label === "" && lifecycleNotice.subtitle) {
    lifecycleNotice = { label: lifecycleNotice.subtitle, subtitle: undefined };
  }

  return { stock, lifecycleNotice };
};
